import { ref, Ref } from 'vue';
import type { MenuOverflowMode } from '../types';
import type { useElementMeasurements } from './useElementMeasurements';
import type { useGPUTransform } from './useGPUTransform';
import type { ProcessedItem } from './useProcessedItems';
import type { useRAFScheduler } from './useRAFScheduler';
import type { useWillChange } from './useWillChange';

interface UseIndicatorOptions {
  mode: Ref<MenuOverflowMode>;
  activeBackground: Ref<string>;
  animationDuration: Ref<number>;
  measurementCache: ReturnType<typeof useElementMeasurements> | null;
  rafScheduler: ReturnType<typeof useRAFScheduler> | null;
  gpuTransform: ReturnType<typeof useGPUTransform> | { 
    isGPUAvailable: () => boolean; 
    applyTransform: () => void; 
    setBaseDimensions: () => void; 
    getTransformState: () => { baseWidth: number };
    reset: () => void;
  };
  willChangeManager: ReturnType<typeof useWillChange> | null;
  enableGPU: Ref<boolean>;
  enableWillChange: Ref<boolean>;
  enableRAF: Ref<boolean>;
}

export function useIndicator(options: UseIndicatorOptions) {
  const {
    mode,
    activeBackground,
    animationDuration,
    measurementCache,
    rafScheduler,
    gpuTransform,
    willChangeManager,
    enableGPU,
    enableWillChange,
    enableRAF,
  } = options;

  const indicatorLeft = ref(0);
  const indicatorWidth = ref(0);
  const indicatorVisible = ref(false);
  const indicatorColor = ref<string>(activeBackground.value);
  const indicatorRef = ref<{ indicatorRef: HTMLElement | null } | null>(null);

  const updateIndicator = async (
    activeId: string | number | null,
    processedItems: ProcessedItem[],
    menuRef: HTMLElement | null,
    sliderRef: HTMLElement | null,
    moreButtonRef: HTMLElement | null,
    itemRefs: Record<string, HTMLElement>
  ) => {
    if (process.env.SERVER) {
      return;
    }

    try {
      if (!activeId || !menuRef) {
        indicatorVisible.value = false;
        if (indicatorRef.value?.indicatorRef) {
          willChangeManager?.remove(indicatorRef.value.indicatorRef);
        }
        return;
      }

      const activeItemData = processedItems.find((item) => item.id === activeId);
      if (!activeItemData) {
        indicatorVisible.value = false;
        if (indicatorRef.value?.indicatorRef) {
          willChangeManager?.remove(indicatorRef.value.indicatorRef);
        }
        return;
      }

      if (activeItemData.isHidden) {
        indicatorVisible.value = false;
        if (indicatorRef.value?.indicatorRef) {
          willChangeManager?.remove(indicatorRef.value.indicatorRef);
        }
        return;
      }

      const itemMode = activeItemData.mode || (activeItemData.bgColor ? 'filled' : 'flat');
      if (activeItemData.hasCustomBg && itemMode === 'filled') {
        indicatorVisible.value = false;
        if (indicatorRef.value?.indicatorRef) {
          willChangeManager?.remove(indicatorRef.value.indicatorRef);
        }
        return;
      }

      // Slider mode
      if (mode.value === 'slider' && sliderRef) {
        const activeItemMeasurement = measurementCache?.getItemMeasurement(activeId);
        if (!activeItemMeasurement) {
          if (enableRAF.value && rafScheduler) {
            rafScheduler.schedule('measure-all', () => {
              try {
                measurementCache?.measureAll(menuRef, itemRefs, null, sliderRef);
                rafScheduler?.schedule('update-indicator', () => updateIndicator(
                  activeId,
                  processedItems,
                  menuRef,
                  sliderRef,
                  moreButtonRef,
                  itemRefs
                ), 'normal');
              } catch (error) {
                if (!process.env.SERVER) {
                  console.error('[Indicator] Error in measure-all:', error);
                }
              }
            }, 'high');
          }
          return;
        }

        indicatorLeft.value = activeItemMeasurement.offsetLeft;
        indicatorWidth.value = activeItemMeasurement.offsetWidth;
        indicatorVisible.value = true;
        indicatorColor.value = activeItemData.indicatorColor || activeBackground.value;

        if (indicatorRef.value?.indicatorRef && enableWillChange.value && gpuTransform.isGPUAvailable()) {
          try {
            willChangeManager?.set(
              indicatorRef.value.indicatorRef,
              ['left', 'width'],
              animationDuration.value
            );
          } catch (error) {
            if (!process.env.SERVER) {
              console.error('[Indicator] GPU transform failed:', error);
            }
          }
        }
        return;
      }

      // Dropdown mode
      if (activeItemData.isHidden) {
        indicatorVisible.value = false;
        if (indicatorRef.value?.indicatorRef) {
          willChangeManager?.remove(indicatorRef.value.indicatorRef);
        }
        return;
      }

      const menuMeasurement = measurementCache?.cache.value.menu;
      const activeItemMeasurement = measurementCache?.getItemMeasurement(activeId);

      if (!menuMeasurement || !activeItemMeasurement) {
        if (enableRAF.value && rafScheduler) {
          rafScheduler.schedule('measure-all', () => {
            try {
              measurementCache?.measureAll(menuRef, itemRefs, moreButtonRef, null);
              rafScheduler?.schedule('update-indicator', () => updateIndicator(
                activeId,
                processedItems,
                menuRef,
                sliderRef,
                moreButtonRef,
                itemRefs
              ), 'normal');
            } catch (error) {
              console.error('[Indicator] Error in measure-all:', error);
            }
          }, 'high');
        }
        return;
      }

      const targetLeft = activeItemMeasurement.left - menuMeasurement.left;
      const targetWidth = activeItemMeasurement.width;

      indicatorLeft.value = targetLeft;
      indicatorWidth.value = targetWidth;
      indicatorVisible.value = true;
      indicatorColor.value = activeItemData.indicatorColor || activeBackground.value;

      if (indicatorRef.value?.indicatorRef && enableGPU.value && gpuTransform?.isGPUAvailable()) {
        try {
          if (enableWillChange.value) {
            willChangeManager?.set(
              indicatorRef.value.indicatorRef,
              ['transform', 'width'],
              animationDuration.value
            );
          }

          if (gpuTransform.getTransformState().baseWidth === 0) {
            gpuTransform.setBaseDimensions(targetWidth, activeItemMeasurement.height);
          }

          gpuTransform.applyTransform(
            indicatorRef.value.indicatorRef,
            targetLeft,
            0,
            targetWidth,
            undefined
          );
        } catch (error) {
          if (!process.env.SERVER) {
            console.error('[Indicator] GPU transform failed:', error);
          }
        }
      }
    } catch (error) {
      if (!process.env.SERVER) {
        console.error('[Indicator] Error in updateIndicator:', error);
      }
      indicatorVisible.value = false;
    }
  };

  return {
    indicatorLeft,
    indicatorWidth,
    indicatorVisible,
    indicatorColor,
    indicatorRef,
    updateIndicator,
  };
}
